import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useDropdownStyles } from "components/FDFilterDropdown/FDFilterDropdownStyles";
import {
  EuiFieldSearch,
  EuiFilterButton,
  EuiPopoverTitle,
  useGeneratedHtmlId,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiText,
  EuiAccordion,
  EuiNotificationBadge,
  EuiIcon,
} from "@elastic/eui";
import { NEGATE_FILTER } from "Consts";
import LookupService from "services/LookupService";
import { CustomCheckbox } from "components/CustomInputs/CustomCheckbox";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import globalFilterTypes from "reduxStore/globalFilter/globalFilter.types";
import FDFilterDropdownFooter from "components/FDFilterDropdown/components/FDFilterDropdownFooter";
import Popover from "components/Popover/Popover";
import "styles/components/filterDropdown/fd_filter_dropdown.css";

const AccordionItem = ({
  option,
  activeFilters,
  filterItemClick,
  classes,
  setActiveFilters,
  onToggle,
  isOpen,
  filterText,
  singleTopLevelSelection,
  singleSelection,
}) => {
  const processedTagOptions = useMemo(() => {
    return option?.values
      ? option?.values?.map((i) => {
          const fullTagName = `${option.name}: ${i?.value}`;
          return {
            ...i,
            name: i?.value,
            fullTagName,
            checked: activeFilters[fullTagName] === "on" ? "on" : undefined,
            key: option?.name,
          };
        })
      : [
          {
            ...option,
            name: option?.name,
            fullTagName: option?.name,
            checked: activeFilters[option?.name] === "on" ? "on" : undefined,
            key: option?.name,
          },
        ];
  }, [option, activeFilters]);

  const filteredTagOptions = useMemo(() => {
    let options = processedTagOptions;

    if (filterText) {
      options = processedTagOptions.filter((tagOption) => {
        const tagValue = tagOption.name || "";
        const tagDisplay = tagOption.display || tagValue;

        return (
          tagValue.toLowerCase().includes(filterText.toLowerCase()) ||
          tagDisplay.toLowerCase().includes(filterText.toLowerCase())
        );
      });
    }

    return options.sort((a, b) => {
      const aDisplay = a.display || a.name || "";
      const bDisplay = b.display || b.name || "";
      return aDisplay.localeCompare(bDisplay);
    });
  }, [processedTagOptions, filterText]);

  const checkedCount = useMemo(() => {
    return processedTagOptions.filter((tagOption) => activeFilters[tagOption.fullTagName] === "on")
      .length;
  }, [processedTagOptions, activeFilters]);

  const areAllSelected = useMemo(() => {
    return processedTagOptions.length > 0 && checkedCount === processedTagOptions.length;
  }, [processedTagOptions.length, checkedCount]);

  const handleCheckboxClick = (e) => {
    if (singleSelection) return;
    e.stopPropagation();
    const newActiveFilters = { ...(!singleTopLevelSelection && activeFilters) };

    if (areAllSelected) {
      processedTagOptions.forEach((tagOption) => {
        newActiveFilters[tagOption.fullTagName] = undefined;
      });
    } else {
      processedTagOptions.forEach((tagOption) => {
        newActiveFilters[tagOption.fullTagName] = "on";
      });
    }

    setActiveFilters(newActiveFilters);
  };

  const handleTagOptionClick = (option) => {
    const parsedSelected = {
      name: option.fullTagName,
      value: option.fullTagName,
      label: option.fullTagName,
      display: option.fullTagName,
      key: option.key,
    };
    filterItemClick(parsedSelected);
  };

  const renderTagOption = (tagOption) => {
    return (
      <div
        key={tagOption.fullTagName}
        className={classes.tagOptionRow}
        onClick={() => handleTagOptionClick(tagOption)}
        style={{ cursor: "pointer" }}>
        <EuiFlexGroup
          style={{
            padding: "0 4px",
          }}
          alignItems="center"
          gutterSize="s">
          <CustomCheckbox
            onClick={() => {}}
            isChecked={activeFilters[tagOption.fullTagName] === "on"}
            isExcluded={activeFilters[tagOption.fullTagName] === "off"}
          />
          <EuiFlexItem>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                fontSize: tagOption?.name?.length > 50 ? "11px" : "14px",
              }}>
              {tagOption?.type && <b>{tagOption?.type}:&nbsp;</b>}
              {tagOption.display ?? tagOption.name}
            </span>
          </EuiFlexItem>
        </EuiFlexGroup>
      </div>
    );
  };

  return (
    <div className={classes.tagAccordionButton}>
      <EuiAccordion
        id={`accordion-${option.name}`}
        buttonContent={
          <EuiFlexGroup
            style={{
              height: "33px",
            }}
            alignItems="center"
            gutterSize="s">
            <CustomCheckbox
              onClick={handleCheckboxClick}
              isChecked={areAllSelected}
              isExcluded={0}
              isPartial={checkedCount > 0}
            />
            <EuiFlexItem>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: option?.name?.length > 50 ? "11px" : "14px",
                }}>
                {option.display ?? option.name}
              </span>
            </EuiFlexItem>
            {filteredTagOptions?.length > 0 && checkedCount > 0 && (
              <EuiFlexItem grow={false}>
                <EuiNotificationBadge>{checkedCount}</EuiNotificationBadge>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        }
        paddingSize="s"
        buttonProps={{
          style: {
            width: "100%",
            display: "block",
          },
        }}
        forceState={isOpen ? "open" : "closed"}
        onToggle={(isOpen) => {
          if (onToggle) {
            filteredTagOptions?.length > 0 ? onToggle(option.name, isOpen) : "";
          }
        }}
        extraAction={null}
        arrowDisplay={filteredTagOptions?.length > 0 ? "right" : "none"}>
        <div style={{ marginTop: "0" }}>{filteredTagOptions.map(renderTagOption)}</div>
      </EuiAccordion>
    </div>
  );
};

const ResourceSelector = ({
  Name = "",
  Field = "",
  AllowExclude = false,
  popoverButtonProps = {},
  singleTopLevelSelection = false,
  isLoading = false,
  disableSorting = false,
  anchorPosition = "upCenter",
  singleSelection = false,
}: any) => {
  const classes = useDropdownStyles();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [items, setItems] = useState<any>([]);
  const [resources, setResources] = useState<any>([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [filterText, setFilterText] = useState<string>("");
  const [stableOrder, setStableOrder] = useState([]);
  const [openAccordions, setOpenAccordions] = useState({});

  const { clientId, GlobalTopFilterObject } = useSelector(globalFilterSelector);

  const currentFilterValue = GlobalTopFilterObject[Field];

  useEffect(() => {
    LookupService.getResourcesV2(clientId).then((result) => {
      const response = [
        {
          display: "Host",
          name: "Host",
          value: "Host",
          id: 0,
          values: result?.data.filter((i) => i.resourceType === "Host"),
        },
        {
          display: "Instance",
          name: "Instance",
          value: "Instance",
          id: 1,
          values: result?.data.filter((i) => i.resourceType === "Instance"),
        },
      ];
      setItems(response || []);
      setResources(result?.data);
    });
  }, [clientId]);

  const publishGlobalFilter = useCallback(
    (values, list) => {
      const newFieldGlobalFilterValues = AllowExclude
        ? Object.entries(values).map(([listItem, value]) => {
            if (value === "off") {
              return `${NEGATE_FILTER}${listItem}`;
            }
            return listItem;
          })
        : Object.entries(values)
            .filter(([_, value]) => value !== "off")
            .map(([listItem, _]) => listItem);

      if (!AllowExclude) setActiveFilters({});

      dispatch({
        payload: {
          [Field]: newFieldGlobalFilterValues.length > 0 ? newFieldGlobalFilterValues : null,
        },
        type: globalFilterTypes.UPDATE_TOP_FILTER,
      });

      dispatch({
        payload: {
          [Field]:
            newFieldGlobalFilterValues.length > 0
              ? newFieldGlobalFilterValues.map((name: string) => {
                  const [_type, _name] = name?.split(":");
                  //@ts-ignore
                  const includesNegate = name.includes(NEGATE_FILTER);
                  const { id, value } = list?.find(
                    (a: any) => _name.includes(a.name) && a.resourceType === _type
                  );
                  return includesNegate ? `${NEGATE_FILTER}${id || value}` : id || value;
                })
              : null,
        },
        type: globalFilterTypes.UPDATE_TOP_FILTER_VALUES,
      });
    },
    [Field]
  );

  useEffect(() => {
    const map = {};
    if (currentFilterValue) {
      for (const filterVal of currentFilterValue) {
        if (filterVal?.startsWith(NEGATE_FILTER)) {
          map[filterVal.replace(NEGATE_FILTER, "")] = "off";
        } else {
          map[filterVal] = "on";
        }
      }

      setActiveFilters(map);
    } else {
      setActiveFilters({});
    }
  }, [currentFilterValue, JSON.stringify(items)]);

  const togglePopover = () => {
    const newIsOpen = !isPopoverOpen;
    if (newIsOpen) {
      recalculateOrder();
    }
    setIsPopoverOpen(newIsOpen);
  };

  const closePopover = () => {
    setIsPopoverOpen(false);
    setOpenAccordions({});
  };

  useEffect(
    () => () => {
      setItems([]);
      setLoading(false);
    },
    []
  );

  const activeFiltersCount = Object.keys(activeFilters).length;

  const filterArrowDownButton = (
    <EuiFilterButton
      isLoading={isLoading}
      className={
        activeFiltersCount == 0
          ? "euiFilterButton__notification_hidden filter_button"
          : "filter_button"
      }
      data-test-subj={`filter-dd-${Name}`}
      hasActiveFilters={activeFiltersCount > 0}
      iconType="arrowDown"
      iconSize="s"
      isSelected={isPopoverOpen}
      numActiveFilters={activeFiltersCount}
      numFilters={items.length}
      onClick={togglePopover}
      {...popoverButtonProps}>
      <span style={{ position: "absolute", top: "5px" }}>
        <EuiIcon type="filter" />
      </span>
      <span style={{ paddingLeft: "20px" }}>{Name}</span>
    </EuiFilterButton>
  );

  const filterItemClick = (item) => {
    const currentChecked = activeFilters[item.name];
    let newChecked;

    if (AllowExclude) {
      if (currentChecked !== undefined) {
        if (currentChecked === "on") {
          newChecked = "off";
        } else if (currentChecked === "off" || currentChecked === "on") {
          newChecked = undefined;
        }
      } else {
        newChecked = "on";
      }
    }

    if (!AllowExclude) {
      newChecked = currentChecked === "on" ? undefined : "on";
    }

    const filterListStatusCopy = { ...activeFilters };

    if (newChecked === undefined) {
      delete filterListStatusCopy[item.name];
    } else {
      filterListStatusCopy[item.name] = newChecked;
    }

    setActiveFilters(filterListStatusCopy);
  };

  const applyFilters = () => {
    setFilterText("");
    publishGlobalFilter(activeFilters, resources);
    closePopover();
    recalculateOrder();
  };

  const cancelFiltersSelection = () => {
    setFilterText("");
    closePopover();
  };

  const serachId = useGeneratedHtmlId();

  const dropdownOptions: any[] = items
    .filter((item: any) => {
      const accordionHeaderMatches =
        filterText.length === 0 ||
        (item.name + "").toLowerCase().includes(filterText.toLowerCase()) ||
        (item?.display ?? "").toLowerCase().includes(filterText.toLowerCase());

      if (accordionHeaderMatches) return true;

      if (item.values && item.values.length > 0) {
        return item.values.some((tagOption) => {
          const tagValue = tagOption.value || "";
          const tagDisplay = tagOption.display || tagValue;

          return (
            tagValue.toLowerCase().includes(filterText.toLowerCase()) ||
            tagDisplay.toLowerCase().includes(filterText.toLowerCase())
          );
        });
      }

      return false;
    })
    .sort((a: any, b: any) => a.name - b.name)
    .map((i) => ({
      ...i,
      label: i.name,
    }));

  const getCheckedCountForOption = (option) => {
    return singleTopLevelSelection
      ? option.values?.reduce((count, item) => {
          const fullTagName = `${option.name}: ${item?.value}`;
          return activeFilters[fullTagName] === "on" || activeFilters[fullTagName] === "off"
            ? count + 1
            : count;
        }, 0) || 0
      : activeFilters[option.name] === "on"
        ? 1
        : 0;
  };

  const recalculateOrder = useCallback(() => {
    const filteredItems = items.filter((item) => {
      return (
        filterText.length === 0 ||
        (item.name + "").toLowerCase().includes(filterText.toLowerCase()) ||
        (item?.display ?? "").toLowerCase().includes(filterText.toLowerCase())
      );
    });

    const newOrder = [...filteredItems].sort((a, b) => {
      const aCheckedCount = getCheckedCountForOption(a);
      const bCheckedCount = getCheckedCountForOption(b);

      if (aCheckedCount > 0 && bCheckedCount === 0) return -1;
      if (aCheckedCount === 0 && bCheckedCount > 0) return 1;

      if (aCheckedCount > 0 && bCheckedCount > 0) {
        return bCheckedCount - aCheckedCount;
      }

      const aText = a.display || a.name;
      const bText = b.display || b.name;
      return aText.localeCompare(bText);
    });

    //@ts-ignore
    setStableOrder(newOrder.map((item) => item.name));
  }, [items, filterText, activeFilters]);

  useEffect(() => {
    if (items.length > 0) {
      recalculateOrder();
    }
  }, [items]);

  const handleAccordionToggle = (optionName, isOpen) => {
    if (isOpen) {
      setOpenAccordions({ [optionName]: true });
    } else {
      setOpenAccordions((prev) => {
        const newOpenAccordions = { ...prev };
        delete newOpenAccordions[optionName];
        return newOpenAccordions;
      });
    }
  };

  const getSortedDropdownOptions = () => {
    if (disableSorting) return dropdownOptions;
    if (stableOrder.length === 0) {
      return dropdownOptions;
    }

    const itemMap = {};
    dropdownOptions.forEach((item) => {
      itemMap[item.name] = item;
    });

    return stableOrder.filter((name) => itemMap[name]).map((name) => itemMap[name]);
  };

  useEffect(() => {
    if (filterText.length > 0) {
      const newOpenAccordions = {};

      items.forEach((item) => {
        const headerMatches =
          (item.name + "").toLowerCase().includes(filterText.toLowerCase()) ||
          (item?.display ?? "").toLowerCase().includes(filterText.toLowerCase());

        if (item.values && item.values.length > 0) {
          const hasMatchingChildren = item.values.some((tagOption) => {
            const tagValue = tagOption.value || "";
            const tagDisplay = tagOption.display || tagValue;

            return (
              tagValue.toLowerCase().includes(filterText.toLowerCase()) ||
              tagDisplay.toLowerCase().includes(filterText.toLowerCase())
            );
          });

          if (headerMatches || hasMatchingChildren) {
            newOpenAccordions[item.name] = true;
          }
        } else if (headerMatches) {
          newOpenAccordions[item.name] = true;
        }
      });

      setOpenAccordions(newOpenAccordions);
    } else if (filterText.length === 0) {
      setOpenAccordions({});
    }
  }, [filterText, items]);

  return (
    <>
      {
        <Popover
          noCloseOnScroll
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation();
              cancelFiltersSelection();
            }
          }}
          anchorPosition={anchorPosition}
          button={filterArrowDownButton}
          onClose={cancelFiltersSelection}
          isOpen={isPopoverOpen}
          ownFocus
          children={
            <div
              style={{
                backgroundColor: "var(--card-background)",
                borderRadius: "var(--box-radius)",
              }}>
              <EuiPopoverTitle className={classes.filterPopoverTitle}>
                <EuiFieldSearch
                  className={`${classes.search_input} filter-search-input`}
                  id={serachId}
                  isClearable
                  onChange={(e) => {
                    setFilterText(e.target.value);
                  }}
                  style={{
                    borderTopLeftRadius: 6,
                    borderTopRightRadius: 6,
                    borderBottom: ".6px solid #d3dae6",
                  }}
                  value={filterText}
                />
              </EuiPopoverTitle>
              <div
                className="euiFilterSelect__items"
                style={{
                  overflowY: "auto",
                  maxHeight: "400px",
                  scrollbarWidth: "thin",
                  minWidth: "300px",
                }}>
                {loading ? (
                  <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
                    <EuiFlexGroup
                      justifyContent="center"
                      alignItems="center"
                      direction="column"
                      gutterSize="s">
                      <EuiFlexItem grow={false}>
                        <EuiLoadingSpinner size="s" />
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiText>
                          <p style={{ fontSize: "var(--fs12)", color: "#516381" }}>Loading data</p>
                        </EuiText>
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  </div>
                ) : getSortedDropdownOptions().length > 0 ? (
                  getSortedDropdownOptions().map((option) => (
                    <AccordionItem
                      key={option.name || option.label}
                      option={option}
                      activeFilters={activeFilters}
                      filterItemClick={filterItemClick}
                      classes={classes}
                      setActiveFilters={setActiveFilters}
                      onToggle={handleAccordionToggle}
                      isOpen={!!openAccordions[option.name]}
                      filterText={filterText}
                      singleTopLevelSelection={singleTopLevelSelection}
                      singleSelection={singleSelection}
                    />
                  ))
                ) : (
                  <div style={{ padding: "16px", textAlign: "center" }}>No data found</div>
                )}
              </div>
              <FDFilterDropdownFooter onApply={applyFilters} onCancel={cancelFiltersSelection} />
            </div>
          }
        />
      }
    </>
  );
};

export default ResourceSelector;
